import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import app, { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "contexts/AuthContext";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";

import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

const functions = getFunctions(app);

function SuperAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [contactSubmissions, setContactSubmissions] = useState([]);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editData, setEditData] = useState({ role: "", isActive: true });

  // Pricing dialog
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingUser, setPricingUser] = useState(null);
  const [pricingData, setPricingData] = useState({
    plan: "standard",
    monthlyRate: "",
    billingCycle: "monthly",
    notes: "",
  });

  // Check admin access
  useEffect(() => {
    if (!user) return;
    const checkAdmin = async () => {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const role = userDoc.data()?.role;
      if (role === "admin" || role === "superadmin") {
        setIsAdmin(true);
        fetchUsers();
        fetchContactSubmissions();
      } else {
        setIsAdmin(false);
      }
      setChecking(false);
    };
    checkAdmin();
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const fn = httpsCallable(functions, "adminGetUsers");
      const result = await fn();
      setUsers(result.data || []);
    } catch (err) {
      setError("Failed to load users: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchContactSubmissions = async () => {
    try {
      const { collection, getDocs, query, orderBy, limit: limitFn } = await import("firebase/firestore");
      const q = query(collection(db, "contactSubmissions"), orderBy("createdAt", "desc"), limitFn(50));
      const snapshot = await getDocs(q);
      setContactSubmissions(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to load submissions:", err);
    }
  };

  const handleEditOpen = (u) => {
    setEditUser(u);
    setEditData({ role: u.role || "user", isActive: u.isActive !== false });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const fn = httpsCallable(functions, "adminUpdateUser");
      await fn({ userId: editUser.id, updates: editData });
      setSuccess(`Updated ${editUser.email}`);
      setEditOpen(false);
      fetchUsers();
    } catch (err) {
      setError("Failed to update: " + err.message);
    }
  };

  const handlePricingOpen = (u) => {
    setPricingUser(u);
    setPricingData({
      plan: u.customPricing?.plan || "standard",
      monthlyRate: u.customPricing?.monthlyRate || "",
      billingCycle: u.customPricing?.billingCycle || "monthly",
      notes: u.customPricing?.notes || "",
    });
    setPricingOpen(true);
  };

  const handlePricingSave = async () => {
    try {
      const fn = httpsCallable(functions, "adminSetCustomPricing");
      await fn({ userId: pricingUser.id, pricing: pricingData });
      setSuccess(`Pricing updated for ${pricingUser.email}`);
      setPricingOpen(false);
      fetchUsers();
    } catch (err) {
      setError("Failed to update pricing: " + err.message);
    }
  };

  const handleToggleActive = async (u) => {
    const newStatus = u.isActive === false ? true : false;
    const label = newStatus ? "activated" : "deactivated";
    if (!window.confirm(`${newStatus ? "Activate" : "Deactivate"} ${u.email}?`)) return;
    try {
      const fn = httpsCallable(functions, "adminUpdateUser");
      await fn({ userId: u.id, updates: { isActive: newStatus } });
      setSuccess(`User ${label}: ${u.email}`);
      fetchUsers();
    } catch (err) {
      setError("Failed: " + err.message);
    }
  };

  // Filter users
  const filteredUsers = users.filter(
    (u) =>
      (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.displayName || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.company || "").toLowerCase().includes(search.toLowerCase())
  );

  const getRoleColor = (role) => {
    switch (role) {
      case "superadmin": return "error";
      case "admin": return "warning";
      case "user": return "info";
      default: return "default";
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "N/A";
    if (ts.toDate) return ts.toDate().toLocaleDateString();
    if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString();
    return new Date(ts).toLocaleDateString();
  };

  if (checking) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <ArgonTypography variant="h6">Checking access...</ArgonTypography>
        </ArgonBox>
      </DashboardLayout>
    );
  }

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column">
          <ArgonTypography variant="h4" fontWeight="bold" color="error" mb={2}>
            Access Denied
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text" mb={3}>
            You do not have admin privileges.
          </ArgonTypography>
          <ArgonButton color="dark" onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </ArgonButton>
        </ArgonBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        {/* Header */}
        <ArgonBox mb={3}>
          <ArgonTypography variant="h4" fontWeight="bold">
            Super Admin
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text">
            Manage users, pricing, and platform settings
          </ArgonTypography>
        </ArgonBox>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setError("")}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setSuccess("")}>{success}</Alert>}

        {/* Stats */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} md={3}>
            <Card>
              <ArgonBox p={2} textAlign="center">
                <PeopleIcon sx={{ fontSize: 32, color: "#5046e5", mb: 1 }} />
                <ArgonTypography variant="h4" fontWeight="bold">{users.length}</ArgonTypography>
                <ArgonTypography variant="caption" color="text">Total Users</ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <ArgonBox p={2} textAlign="center">
                <CheckCircleIcon sx={{ fontSize: 32, color: "#22c55e", mb: 1 }} />
                <ArgonTypography variant="h4" fontWeight="bold">
                  {users.filter((u) => u.isActive !== false).length}
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text">Active Users</ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <ArgonBox p={2} textAlign="center">
                <AttachMoneyIcon sx={{ fontSize: 32, color: "#06d6a0", mb: 1 }} />
                <ArgonTypography variant="h4" fontWeight="bold">
                  {users.filter((u) => u.subscriptionStatus === "active").length}
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text">Subscribed</ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <ArgonBox p={2} textAlign="center">
                <MailOutlineIcon sx={{ fontSize: 32, color: "#818cf8", mb: 1 }} />
                <ArgonTypography variant="h4" fontWeight="bold">{contactSubmissions.length}</ArgonTypography>
                <ArgonTypography variant="caption" color="text">Inquiries</ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card>
          <ArgonBox px={3} pt={2}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)}>
              <Tab label="Users" />
              <Tab label="Inquiries" />
            </Tabs>
          </ArgonBox>
          <Divider />

          {/* Users Tab */}
          {tab === 0 && (
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <ArgonInput
                  placeholder="Search users..."
                  icon={{ component: "search", direction: "left" }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ maxWidth: 350 }}
                />
                <ArgonButton color="dark" size="small" onClick={fetchUsers} disabled={loading}>
                  {loading ? "Loading..." : "Refresh"}
                </ArgonButton>
              </ArgonBox>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Subscription</TableCell>
                      <TableCell>Pricing</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <ArgonTypography variant="button" fontWeight="medium">
                            {u.displayName || "No Name"}
                          </ArgonTypography>
                          <ArgonTypography variant="caption" display="block" color="text">
                            {u.email}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">{u.company || "-"}</ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">{u.phone || "-"}</ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <Chip label={u.role || "user"} color={getRoleColor(u.role)} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={u.isActive !== false ? "Active" : "Inactive"}
                            color={u.isActive !== false ? "success" : "default"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={u.subscriptionStatus || "none"}
                            color={u.subscriptionStatus === "active" ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {u.customPricing ? (
                            <ArgonTypography variant="caption" fontWeight="medium">
                              ${u.customPricing.monthlyRate}/mo
                            </ArgonTypography>
                          ) : (
                            <ArgonTypography variant="caption" color="text">Standard</ArgonTypography>
                          )}
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">{formatDate(u.createdAt)}</ArgonTypography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit Role">
                            <IconButton size="small" onClick={() => handleEditOpen(u)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Set Pricing">
                            <IconButton size="small" onClick={() => handlePricingOpen(u)}>
                              <AttachMoneyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={u.isActive !== false ? "Deactivate" : "Activate"}>
                            <IconButton size="small" onClick={() => handleToggleActive(u)}>
                              {u.isActive !== false ? <BlockIcon fontSize="small" color="error" /> : <CheckCircleIcon fontSize="small" color="success" />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <ArgonTypography variant="body2" color="text" py={3}>
                            {loading ? "Loading users..." : "No users found"}
                          </ArgonTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </ArgonBox>
          )}

          {/* Inquiries Tab */}
          {tab === 1 && (
            <ArgonBox p={3}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Portfolio / Interest</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contactSubmissions.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell>
                          <Chip
                            label={s.type || "access"}
                            color={s.type === "demo" ? "info" : s.type === "invest" ? "warning" : "success"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption" fontWeight="medium">{s.name}</ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption" component="a" href={`mailto:${s.email}`} color="info">
                            {s.email}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">{s.company}</ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">{s.portfolioSize || "-"}</ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption" sx={{ maxWidth: 200, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.message || "-"}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">{formatDate(s.createdAt)}</ArgonTypography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {contactSubmissions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <ArgonTypography variant="body2" color="text" py={3}>
                            No inquiries yet
                          </ArgonTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </ArgonBox>
          )}
        </Card>
      </ArgonBox>

      {/* Edit Role Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <ArgonBox mt={1}>
            <ArgonTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
              {editUser?.email}
            </ArgonTypography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={editData.role}
                label="Role"
                onChange={(e) => setEditData({ ...editData, role: e.target.value })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="superadmin">Super Admin</MenuItem>
              </Select>
            </FormControl>
          </ArgonBox>
        </DialogContent>
        <DialogActions>
          <ArgonButton color="secondary" size="small" onClick={() => setEditOpen(false)}>Cancel</ArgonButton>
          <ArgonButton color="dark" size="small" onClick={handleEditSave}>Save</ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Pricing Dialog */}
      <Dialog open={pricingOpen} onClose={() => setPricingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Custom Pricing</DialogTitle>
        <DialogContent>
          <ArgonBox mt={1}>
            <ArgonTypography variant="caption" fontWeight="bold" display="block" mb={2}>
              {pricingUser?.email} ({pricingUser?.company || "No company"})
            </ArgonTypography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Plan</InputLabel>
              <Select
                value={pricingData.plan}
                label="Plan"
                onChange={(e) => setPricingData({ ...pricingData, plan: e.target.value })}
              >
                <MenuItem value="starter">Starter</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Monthly Rate ($)"
              type="number"
              value={pricingData.monthlyRate}
              onChange={(e) => setPricingData({ ...pricingData, monthlyRate: e.target.value })}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Billing Cycle</InputLabel>
              <Select
                value={pricingData.billingCycle}
                label="Billing Cycle"
                onChange={(e) => setPricingData({ ...pricingData, billingCycle: e.target.value })}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={2}
              value={pricingData.notes}
              onChange={(e) => setPricingData({ ...pricingData, notes: e.target.value })}
            />
          </ArgonBox>
        </DialogContent>
        <DialogActions>
          <ArgonButton color="secondary" size="small" onClick={() => setPricingOpen(false)}>Cancel</ArgonButton>
          <ArgonButton color="dark" size="small" onClick={handlePricingSave}>Save Pricing</ArgonButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default SuperAdmin;
